import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import useLinker from "../hooks/linker";
import useAuthService from "../hooks/authService";
import usePermissions from "../hooks/permissions";
import Permissions from "../configs/permissions";

// Mobile replacement for mood-ui/Navbar. The desktop navbar ships a burger
// button with no handler and a menu hard-coded to `hidden md:hidden`, so the
// top-level sections are unreachable on a phone. This renders the same
// permission-gated destinations in a working off-canvas drawer.
const MobileNavbar = () => {
  const { hasAccess, hasAtLeastOnePermission } = usePermissions();
  const { createListUrl } = useLinker();
  const { logout } = useAuthService();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);

  // Close on navigation, otherwise the drawer stays over the new page.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Lock background scroll while the drawer is open.
  useEffect(() => {
    document.body.classList.toggle("vb-m-nav-open", isOpen);
    return () => document.body.classList.remove("vb-m-nav-open");
  }, [isOpen]);

  const section = location.pathname.split("/")[1] || "";

  const items = [];

  if (hasAccess(Permissions.Query.Clients))
    items.push({
      url: createListUrl("clients"),
      label: "Clients",
      matches: ["", "clients", "client"],
    });

  if (hasAccess(Permissions.Query.Users))
    items.push({
      url: createListUrl("users"),
      label: "Users",
      matches: ["users", "user"],
    });

  if (
    hasAccess(Permissions.Query.Merchants) ||
    hasAccess(Permissions.Query.Offers) ||
    hasAccess(Permissions.Query.TilloOffers) ||
    hasAccess(Permissions.Query.Sections) ||
    hasAccess(Permissions.Query.Tags) ||
    hasAccess(Permissions.Query.Images)
  )
    items.push({
      url: createListUrl("merchants"),
      label: "Content",
      matches: [
        "merchants",
        "merchant",
        "offers",
        "offer",
        "sections",
        "section",
        "tags",
        "images",
      ],
    });

  if (hasAccess(Permissions.Query.Surveys))
    items.push({
      url: createListUrl("surveys"),
      label: "Surveys",
      matches: ["surveys", "survey", "questions", "question"],
    });

  if (
    hasAtLeastOnePermission([
      Permissions.Query.TenantQAFlowReport,
      Permissions.Query.TenantQAStatsReport,
      Permissions.Query.UserActivityEventsFlowReport,
      Permissions.Query.RunningActiveCampaignUpdatesReport,
      Permissions.Query.InvoiceReport,
    ])
  )
    items.push({
      url: createListUrl("reports"),
      label: "Reports",
      matches: ["reports", "report"],
    });

  if (
    hasAtLeastOnePermission([
      Permissions.Query.MainAppSettings,
      Permissions.Query.SurveyAndGlobalQuestions,
      Permissions.Query.SystemUsers,
      Permissions.Query.Roles,
    ])
  )
    items.push({
      url: "/settings",
      label: "Settings",
      matches: ["settings"],
    });

  const activeItem = items.find((item) => item.matches.includes(section));

  return (
    <React.Fragment>
      <nav className="vb-m-topbar">
        <button
          type="button"
          className="vb-m-iconbtn"
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          <span
            className={`mdi ${isOpen ? "mdi-close" : "mdi-menu"} vb-m-iconbtn-glyph`}
          ></span>
        </button>

        <Link to="/" className="vb-m-brand">
          <img
            src="https://control.vaboo.co.uk/images/vaboo.png"
            alt="Vaboo Control"
          />
        </Link>

        <span className="vb-m-topbar-section">{activeItem?.label || ""}</span>
      </nav>

      <div
        className={`vb-m-scrim ${isOpen ? "vb-m-scrim-open" : ""}`}
        onClick={() => setIsOpen(false)}
      ></div>

      <aside className={`vb-m-drawer ${isOpen ? "vb-m-drawer-open" : ""}`}>
        <div className="vb-m-drawer-head">Vaboo Control</div>

        <nav className="vb-m-drawer-nav">
          {items.map((item) => (
            <Link
              key={item.label}
              to={item.url}
              className={`vb-m-drawer-item ${
                item === activeItem ? "vb-m-drawer-item-active" : ""
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <a href="/login" className="vb-m-drawer-logout" onClick={logout}>
          Log out
        </a>
      </aside>
    </React.Fragment>
  );
};

export default MobileNavbar;
